import { useRef } from "react";
import { gsap, SplitText, useGSAP, MOTION_OK, sel } from "../lib/motion";
import { profile } from "../data/profile";
import { introWillPlay, INTRO_DELAY } from "./Intro";
import portrait from "../assets/portrait.jpg";
import s from "./Hero.module.css";

export default function Hero() {
  const root = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        const split = SplitText.create(sel(s.name), { type: "chars", mask: "chars" });
        const tl = gsap.timeline({
          defaults: { ease: "expo.out" },
          delay: introWillPlay() ? INTRO_DELAY : 0.1,
        });

        // Load sequence: the name lands first, then the photo wipes in over its colour block.
        tl.from(split.chars, { yPercent: 110, duration: 1, stagger: 0.035 }, 0)
          .from(sel(s.role), { autoAlpha: 0, y: 14, duration: 0.7 }, 0.15)
          .from(sel(s.block), { scaleY: 0, transformOrigin: "50% 100%", duration: 0.9 }, 0.25)
          .from(sel(s.photo), { clipPath: "inset(100% 0 0 0)", duration: 1.2 }, 0.4)
          .from(`${sel(s.photo)} img`, { scale: 1.18, duration: 1.6 }, 0.4)
          .from(sel(s.statement), { autoAlpha: 0, y: 22, duration: 0.9 }, 0.55)
          .from(`${sel(s.ctas)} > *`, { autoAlpha: 0, y: 16, duration: 0.7, stagger: 0.08 }, 0.75);

        // Depth on scroll: the photo drifts slower than the copy.
        gsap.to(`${sel(s.photo)} img`, {
          yPercent: 8,
          ease: "none",
          scrollTrigger: { trigger: root.current, start: "top top", end: "bottom top", scrub: true },
        });
        gsap.to(sel(s.block), {
          yPercent: -14,
          ease: "none",
          scrollTrigger: { trigger: root.current, start: "top top", end: "bottom top", scrub: true },
        });
      });
    },
    { scope: root }
  );

  return (
    <section ref={root} id="top" className={s.hero} tabIndex={-1}>
      <div className={`container ${s.inner}`}>
        <div className={s.copy}>
          <p className={s.role}>{profile.current}</p>
          <h1 className={s.name}>{profile.name}</h1>
          <p className={s.statement}>{profile.statement}</p>
          <div className={s.ctas}>
            <a href={`mailto:${profile.email}`} className="btn btn-primary">
              Email me
            </a>
            <a href="#work" className="btn btn-ghost">
              See the work
            </a>
          </div>
        </div>

        <div className={s.visual}>
          <div className={s.block} aria-hidden="true" />
          <div className={s.photo}>
            <img
              src={portrait}
              width={960}
              height={1200}
              alt={`${profile.name} at a desk in a dark suit, smiling, laptop open`}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
